import React, { useMemo, useState } from 'react';
import { Loader, Search, X } from 'lucide-react';
import { Modal } from './ui/Modal';
import { useChatStore } from '../store/chatStore';
import chatService from '../services/chatService';
import { channelDisplay, dmOther } from './chatHelpers';
import UserAvatar from './ui/UserAvatar';
import { buildTaskShareMetadata, channelsForTeamShare } from '../utils/taskChatShare';
import './Chat.css';
import './boards/BoardFilterBar.css';

function newUid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * "Send this task to chat".
 *
 * Separate from ShareToChatModal rather than another case inside it, because
 * the two have opposite recipient rules. That one exists to reach people who
 * do NOT have the resource — it mints a scoped share link for them. This one
 * may only reach people who already do: access to a task is team membership,
 * and a link that granted it would be a second way in that outlives the roster.
 *
 * So the list is not filtered for convenience. Conversations containing anyone
 * outside the team are absent because sending there would be wrong, and the
 * empty state says which rule removed them.
 */
export default function ShareTaskToChatModal({ isOpen, onClose, task, board, members, showToast }) {
  const channels = useChatStore((s) => s.channels);
  const setActiveChannel = useChatStore((s) => s.setActiveChannel);
  const currentUserId = useChatStore((s) => s.currentUserId);
  const [sending, setSending] = useState(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');

  const teamMemberIds = useMemo(() => (members || []).map((m) => String(m.id)), [members]);
  const eligible = useMemo(
    () => channelsForTeamShare(channels, teamMemberIds),
    [channels, teamMemberIds]
  );
  const hiddenCount = (channels || []).length - eligible.length;

  // Filtering happens AFTER the eligibility rule, never instead of it: typing a
  // name must not be able to surface a conversation that contains someone
  // outside the team.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return eligible;
    return eligible.filter((c) => channelDisplay(c, currentUserId).name.toLowerCase().includes(q));
  }, [eligible, query, currentUserId]);

  const send = async (channel) => {
    const metadata = buildTaskShareMetadata({ task, board });
    if (!metadata) {
      // Only reachable for a private board, which has no team to share within.
      setError('Only tasks on a team board can be shared.');
      return;
    }
    setSending(channel.id);
    setError('');
    try {
      await chatService.sendMessage(channel.id, { uid: newUid(), body: '', kind: 'share', metadata });
      showToast?.('Task sent', 'success');
      setActiveChannel?.(channel.id);
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Could not send the task.');
    } finally {
      setSending(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send task to a teammate" size="sm">
      <div className="chat-share-modal">
        {eligible.length === 0 ? (
          <p className="chat-share-empty">
            {hiddenCount > 0
              ? 'No direct message with someone on this team yet. Tasks are sent one-to-one, to people who can open them.'
              : 'No direct message with someone on this team yet.'}
          </p>
        ) : (
          <>
            <p className="chat-share-hint">
              Direct messages only, and only with people on this team — the card
              is unreadable to anyone else.
            </p>
            {/* Shown once there are enough rows to be worth searching. Below
                that the box is more chrome than help. */}
            {eligible.length > 5 && (
              <div className="task-board-filter-search chat-share-filter">
                <Search size={13} aria-hidden />
                <input
                  type="text"
                  value={query}
                  placeholder="Filter people…"
                  aria-label="Filter people"
                  onChange={(e) => setQuery(e.target.value)}
                />
                {!!query && (
                  <button type="button" aria-label="Clear filter" onClick={() => setQuery('')}>
                    <X size={12} />
                  </button>
                )}
              </div>
            )}
            {visible.length === 0 && (
              <p className="chat-share-empty">Nobody matches “{query}”.</p>
            )}
            <ul className="chat-share-list">
              {visible.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="chat-share-row"
                    disabled={sending != null}
                    onClick={() => send(c)}
                  >
                    {/* The real avatar, the same one chat shows — a Hash/Users
                        glyph made every row look alike, and picking the right
                        conversation is the only decision this modal asks for.
                        A DM is a PERSON, so it gets their face; a channel gets
                        its own image or initials. */}
                    {/* Always a person: channelsForTeamShare returns DMs
                        only, so there is no channel branch to write. */}
                    <UserAvatar
                      className="chat-share-row-avatar"
                      userId={dmOther(c, currentUserId)?.user_id}
                      name={channelDisplay(c, currentUserId).name}
                      size={32}
                    />
                    <span className="chat-share-row-name">{channelDisplay(c, currentUserId).name}</span>
                    {sending === c.id && <Loader size={13} className="animate-spin" aria-hidden />}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        {error && <p className="chat-share-error">{error}</p>}
      </div>
    </Modal>
  );
}
